import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AtSign, Check, X } from "lucide-react";
import api, { API } from "../lib/api";

function relTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 45) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * MentionsBell — topbar bell with unread count + popover list.
 * Auto-hides when the calling user can't access mentions (Free/Starter).
 *
 * Visibility: pass `enabled` only after the parent has confirmed the
 * user is on Pro/Enterprise/Admin. Component still self-protects against
 * 402 responses to stay invisible if misconfigured.
 */
export default function MentionsBell({ enabled = true }) {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true); // false ⇒ hide bell
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await api.get("/mentions/unread-count");
      setUnread(res.data?.unread || 0);
    } catch (e) {
      if (e?.response?.status === 402) setAvailable(false);
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/mentions?status=all&limit=20");
      setItems(res.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refreshCount();
    const intv = setInterval(refreshCount, 30000);
    // Listen for SSE messages — when one arrives, re-poll the count
    const tok = localStorage.getItem("rv_token");
    let es = null;
    if (tok) {
      try {
        es = new EventSource(`${API}/messages/stream?token=${encodeURIComponent(tok)}`);
        es.addEventListener("message", () => refreshCount());
      } catch {
        // ignore
      }
    }
    return () => {
      clearInterval(intv);
      if (es) es.close();
    };
  }, [enabled, refreshCount]);

  // Click-outside dismiss
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!enabled || !available) return null;

  const togglePopover = () => {
    const next = !open;
    setOpen(next);
    if (next) refreshList();
  };

  const handleItemClick = async (m) => {
    setOpen(false);
    if (!m.read_at) {
      try {
        await api.post(`/mentions/${m.id}/read`);
      } catch {
        // ignore
      }
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, read_at: new Date().toISOString() } : x)));
    }
    if (m.link) navigate(m.link);
  };

  const markAllRead = async () => {
    try {
      await api.post("/mentions/mark-all-read");
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={togglePopover}
        data-testid="mentions-bell-btn"
        aria-label="Open mentions"
        className="relative p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100"
      >
        <AtSign className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="mentions-bell-unread"
            className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold leading-none"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={popRef}
          data-testid="mentions-bell-popover"
          className="absolute right-0 mt-2 w-96 max-h-[480px] overflow-hidden bg-white border border-slate-200 rounded-md shadow-lg z-50 flex flex-col"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-slate-500" />
              <span className="font-semibold text-slate-900 text-sm">Mentions</span>
            </div>
            <button
              onClick={markAllRead}
              data-testid="mentions-mark-all-read-btn"
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              disabled={unread === 0}
            >
              <Check className="w-3.5 h-3.5" /> Mark all read
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="p-6 text-center text-sm text-slate-500">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="mentions-empty">
                No mentions yet. Tag a teammate with <span className="bg-slate-100 px-1.5 py-0.5 rounded">@name</span> in chat.
              </div>
            )}
            {!loading &&
              items.map((m) => {
                const unreadRow = !m.read_at;
                return (
                  <button
                    key={m.id}
                    onClick={() => handleItemClick(m)}
                    data-testid={`mentions-row-${m.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                      unreadRow ? "bg-amber-50/50" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        <AtSign className="w-3.5 h-3.5 inline -mt-0.5 text-amber-600 mr-1" />
                        {m.mentioner_label} mentioned you
                      </div>
                      {unreadRow && (
                        <span className="w-2 h-2 rounded-full bg-rose-600 mt-1.5 shrink-0" aria-label="unread" />
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{m.source_excerpt}</div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      {m.source_type.replace("_", " ")} · {relTime(m.created_at)}
                    </div>
                  </button>
                );
              })}
          </div>
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Mentions stay private to your account.</span>
            <button
              onClick={() => setOpen(false)}
              className="hover:text-slate-900 inline-flex items-center gap-1"
              aria-label="Close"
            >
              <X className="w-3 h-3" /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
